"use client";

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Minus, Send, X, MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { FramedAvatar } from "@/components/FramedAvatar";
import { GifPicker } from "@/components/ui/gif-picker";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { listMessages, sendMessage } from "@/lib/chat.functions";
import { cn } from "@/lib/utils";

const EVENT = "viny:open-chat";

export type ChatDockPeer = {
  id: string;
  username: string;
  display_name?: string | null;
  avatar_url?: string | null;
  is_premium?: boolean;
};

/** Abre uma janelinha de conversa (desktop) ou a página de mensagens (celular). */
export function openChatWindow(conversationId: string, peer?: ChatDockPeer) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { conversationId, peer } }));
}

type Win = { conversationId: string; peer?: ChatDockPeer; minimized: boolean };

export function ChatDock() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { isAuthenticated } = useCurrentUser();
  const [wins, setWins] = useState<Win[]>([]);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent).detail as { conversationId: string; peer?: ChatDockPeer };
      if (!detail?.conversationId) return;
      if (isMobile) {
        navigate({ to: "/chat", search: { c: detail.conversationId } });
        return;
      }
      setWins((prev) => {
        const exists = prev.find((w) => w.conversationId === detail.conversationId);
        if (exists)
          return prev.map((w) =>
            w.conversationId === detail.conversationId ? { ...w, minimized: false } : w,
          );
        return [
          ...prev.slice(-2),
          { conversationId: detail.conversationId, peer: detail.peer, minimized: false },
        ];
      });
    };
    window.addEventListener(EVENT, onOpen);
    return () => window.removeEventListener(EVENT, onOpen);
  }, [isMobile, navigate]);

  if (isMobile || !isAuthenticated || wins.length === 0) return null;

  return (
    <div className="pointer-events-none fixed bottom-0 right-4 z-[60] hidden items-end gap-3 lg:flex">
      {wins.map((w) => (
        <ChatWindow
          key={w.conversationId}
          win={w}
          onToggleMin={() =>
            setWins((prev) =>
              prev.map((p) =>
                p.conversationId === w.conversationId ? { ...p, minimized: !p.minimized } : p,
              ),
            )
          }
          onClose={() =>
            setWins((prev) => prev.filter((p) => p.conversationId !== w.conversationId))
          }
        />
      ))}
    </div>
  );
}

function ChatWindow({
  win,
  onToggleMin,
  onClose,
}: {
  win: Win;
  onToggleMin: () => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { user } = useCurrentUser();
  const fetchMsgs = useServerFn(listMessages);
  const sendFn = useServerFn(sendMessage);
  const [text, setText] = useState("");
  const [unread, setUnread] = useState(0);
  const endRef = useRef<HTMLDivElement | null>(null);

  const q = useQuery({
    queryKey: ["chat-dock-messages", win.conversationId],
    queryFn: () => fetchMsgs({ data: { conversationId: win.conversationId } }),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const channel = supabase
      .channel(`dock-${win.conversationId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `conversation_id=eq.${win.conversationId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: ["chat-dock-messages", win.conversationId] });
          if (win.minimized) setUnread((n) => n + 1);
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc, win.conversationId, win.minimized]);

  useEffect(() => {
    if (!win.minimized) setUnread(0);
  }, [win.minimized]);

  useEffect(() => {
    if (!win.minimized) endRef.current?.scrollIntoView({ block: "end" });
  }, [q.data, win.minimized]);

  const send = useMutation({
    mutationFn: (vars: { body?: string; mediaUrl?: string }) =>
      sendFn({
        data: {
          conversationId: win.conversationId,
          body: vars.body,
          mediaUrl: vars.mediaUrl,
          mediaType: vars.mediaUrl ? "gif" : undefined,
        },
      }),
    onSuccess: () => {
      setText("");
      qc.invalidateQueries({ queryKey: ["chat-dock-messages", win.conversationId] });
      qc.invalidateQueries({ queryKey: ["conversations"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const name = win.peer?.display_name || win.peer?.username || "Conversa";
  const msgs = q.data ?? [];

  return (
    <div
      className={cn(
        "pointer-events-auto flex w-[320px] flex-col overflow-hidden rounded-t-2xl border border-[color:var(--hairline)] bg-background shadow-2xl",
        win.minimized ? "h-[46px]" : "h-[420px]",
      )}
    >
      <header
        onClick={() => win.minimized && onToggleMin()}
        className="flex shrink-0 items-center gap-2 border-b border-[color:var(--hairline)] bg-surface/60 px-2.5 py-2"
      >
        {win.peer ? (
          <FramedAvatar
            userId={win.peer.id}
            src={win.peer.avatar_url ?? null}
            username={win.peer.username}
            isPremium={win.peer.is_premium}
            size={26}
          />
        ) : (
          <MessageCircle className="h-4 w-4 text-primary" />
        )}
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">{name}</span>
        {unread > 0 && (
          <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold text-primary-foreground">
            {unread}
          </span>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleMin();
          }}
          className="rounded-full p-1 text-muted-foreground hover:bg-surface hover:text-foreground"
          aria-label={win.minimized ? "Expandir conversa" : "Minimizar conversa"}
        >
          <Minus className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="rounded-full p-1 text-muted-foreground hover:bg-surface hover:text-foreground"
          aria-label="Fechar conversa"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </header>

      {!win.minimized && (
        <>
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2.5">
            {q.isLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : msgs.length === 0 ? (
              <p className="py-6 text-center text-[12px] text-muted-foreground">
                Diga um oi para começar.
              </p>
            ) : (
              msgs.map((m: any) => {
                const mine = m.sender_id === user?.id;
                return (
                  <div key={m.id} className={cn("flex", mine ? "justify-end" : "justify-start")}>
                    <div
                      className={cn(
                        "max-w-[80%] rounded-2xl px-3 py-1.5 text-[13px]",
                        mine
                          ? "bg-primary text-primary-foreground"
                          : "bg-surface text-foreground",
                      )}
                    >
                      {m.body && <span className="whitespace-pre-wrap break-words">{m.body}</span>}
                      {m.media_url && (
                        <img src={m.media_url} alt="" className="mt-1 max-h-40 rounded-lg" />
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={endRef} />
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (text.trim()) send.mutate({ body: text });
            }}
            className="flex shrink-0 items-center gap-1.5 border-t border-[color:var(--hairline)] p-2"
          >
            <GifPicker size="xs" onPick={(g) => send.mutate({ mediaUrl: g.url })} />
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Escreva uma mensagem"
              style={{ fontSize: 16 }}
              className="min-w-0 flex-1 rounded-full border border-[color:var(--hairline)] bg-surface/60 px-3 py-1.5 text-[13px] focus:border-primary/60 focus:outline-none"
            />
            <button
              type="submit"
              disabled={send.isPending || !text.trim()}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground disabled:opacity-50"
              aria-label="Enviar"
            >
              {send.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3 w-3" />
              )}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
